import React, { useEffect, useState } from 'react';
import './GithubConnectError.css';

/**
 * Banner for a failed GitHub connect.
 *
 * The GitHub callback reports a rejected connect by redirecting to
 * #/dashboard/vendors?githubError=<message> (GitHubAuthController.vendorsRedirect). Nothing read
 * that parameter, so the user saw no change at all and the reason was only visible in the address
 * bar. The first fix read it inside VendorCards, which mounts at /dashboard/vendors/cards — not the
 * /dashboard/vendors the callback actually returns to — so the message still never appeared and the
 * parameter was never cleared, which is why it survived a refresh.
 *
 * Keeping it in one component means every page that can receive the redirect shows the same banner.
 * Mount it near the top of the page body; it renders nothing when there is no error.
 */
const GithubConnectError = () => {
  const [error, setError] = useState('');

  useEffect(() => {
    const hash = window.location.hash || '';
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return;
    const params = new URLSearchParams(hash.slice(qIdx + 1));
    const err = params.get('githubError');
    if (!err) return;
    setError(err);
    // Drop the parameter so a refresh — or a later successful connect — does not replay a stale error.
    params.delete('githubError');
    const rest = params.toString();
    const cleanedHash = hash.slice(0, qIdx) + (rest ? `?${rest}` : '');
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + cleanedHash,
    );
  }, []);

  if (!error) return null;

  return (
    <div className="gh-connect-error" role="alert">
      <i className="fas fa-exclamation-triangle" />
      <span>{error}</span>
      <button
        type="button"
        className="gh-connect-error-close"
        aria-label="Dismiss"
        onClick={() => setError('')}
      >
        ×
      </button>
    </div>
  );
};

export default GithubConnectError;
